import { useCallback, useEffect, useState } from 'react';

import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker.entry';
import { PdfPageStateProps } from '../utils/interfaces';

interface PDFRendererProps {
  fileUrl: string;
  scale: number;
  rotation: number;
  setListDocumentsViewerState: React.Dispatch<
    React.SetStateAction<PdfPageStateProps>
  >;
  showSnackbar: (args: { type: 'error'; message: string }) => void;
}

export const usePDFRenderer = ({
  fileUrl,
  scale,
  rotation,
  setListDocumentsViewerState,
  showSnackbar,
}: PDFRendererProps) => {
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);

  useEffect(() => {
    if (!fileUrl) return;

    const loadPDF = async () => {
      try {
        const pdf = await pdfjsLib.getDocument(fileUrl).promise;
        setPdfDoc(pdf);
        setListDocumentsViewerState(prev => ({
          ...prev,
          numPages: pdf.numPages,
        }));
      } catch (error) {
        showSnackbar({
          type: 'error',
          message: `Erro ao carregar o PDF: ${error}`,
        });
      }
    };
    loadPDF();
  }, [fileUrl]);

  const renderPageCanvas = useCallback(
    async (pageIndex: number, canvas: HTMLCanvasElement | null) => {
      if (!pdfDoc || !canvas) return;

      const page = await pdfDoc.getPage(pageIndex);
      const viewport = page.getViewport({ scale, rotation });
      const context = canvas.getContext('2d');

      if (context) {
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext: context, viewport }).promise;
        page.cleanup();
      }
    },
    [pdfDoc, scale, rotation]
  );

  return { pdfDoc, renderPageCanvas };
};
